import Page from 'modules/app/components/Page';
import ProfileMenuHeader from 'modules/app/components/ProfileMenu/ProfileMenuHeader';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import s from './BookingSuccess.module.scss';
import { ReactComponent as CheckMark } from 'images/new-icons/check-mark-big.svg';
import Button from 'components/shared/Button';
import useGetBookingsById from 'sdk/hooks/shop/useGetBookingsById';
import { parse, format } from 'date-fns';
import CancelAppointment from './CancelAppointment';
import useCancelBooking from 'sdk/hooks/shop/useCancelBooking';
import { supabase } from 'sdk/api';
import { useTranslation } from 'react-i18next';
import CalendarModal from 'components/shared/CalendarModal/CalendarModal';
import { CalendarEvent } from 'components/utils/calendarExport';

const formatDateTime = (date, startTime, endTime) => {
  const dateObj = parse(date, 'yyyy-MM-dd', new Date());
  const startTimeObj = parse(startTime, 'HH:mm:ss', new Date());
  const endTimeObj = parse(endTime, 'HH:mm:ss', new Date());
  const formattedDate = format(dateObj, 'dd MMM yyyy');
  const formattedStartTime = format(startTimeObj, 'h:mm a');
  const formattedEndTime = format(endTimeObj, 'h:mm a');

  return `${formattedDate}, ${formattedStartTime} - ${formattedEndTime}`;
};

const BookingSuccess = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation() as any;
  const { data } = useGetBookingsById(id);
  const [showCancel, setShowCancel] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [canceledOption, setCanceledOption] = useState('');
  const [cancelNote, setCancelNote] = useState('');

  const { mutate: cancelBooking } = useCancelBooking(
      id,
      canceledOption === 'Other' ? canceledOption : cancelNote,
  );

  const service = data?.service;
  const team = data?.team_id;
  const recordId = data?.record_id;

  const handleCancelBooking = async () => {
    try {
      await cancelBooking(
          { id, reason: canceledOption === 'Other' ? cancelNote : canceledOption },
          {
            onError: error => {
              console.error('Failed to update booking:', error);
              throw error;
            },
          },
      );

      const { error: availabilityError } = await supabase
          .from('teams_availability')
          .update({
            is_available: true,
            record_id: null,
          })
          .eq('record_id', recordId);

      if (availabilityError) {
        console.error(`Error updating availability for team:`, availabilityError);
        throw availabilityError;
      }

      navigate('/feed');
    } catch (error) {
      console.error('Error in cancel booking process:', error);
    }
  };

  const calendarEvent: CalendarEvent | null = data ? {
    date: data.date,
    startTime: data.start_time,
    endTime: data.end_time,
    title: `${service?.name || 'Appointment'} at ${data?.services?.salons_profile?.name || 'Salon'}`,
    description: `${service?.name || 'Service'} with ${team?.full_name || 'specialist'}`,
    location: data.services?.salons_profile?.full_address || '',
  } : null;

  return (
      <div className={s.container}>
        <Page activeLink="/venues">
          <ProfileMenuHeader onClose={() => navigate("/")} text={'appointments_bookings'} />
          <div className={s.content}>
            <div className={s.content_success_img}>
              <CheckMark />
            </div>
            <div className={s.content_success_text}>Your appointment has been booked!</div>
            <div className={s.edit_appointment}>
              <div className={s.edit_appointment_title}>Date And Time</div>
              <div className={s.edit_appointment_time}>
                {data?.start_time ? formatDateTime(data?.date, data?.start_time, data?.end_time) : ''}
              </div>
            </div>
            <div className={s.info}>
              <div className={s.info_service}>
                <div className={s.info_service_header}>
                  <img src={service?.image} alt="" />
                  <div className={s.info_service_header_title}>{service?.name}</div>
                </div>
              </div>
              <div className={s.info_service_title}>{team?.job_title}</div>
            </div>
            <div className={s.contact_container}>
              <div className={s.contact_container_item}>
                <span className={s.contact_container_item_label}>Services</span>
                <span className={s.contact_container_item_value}>{service?.menu_category?.name}</span>
              </div>
              <div className={s.contact_item}>
                <span className={s.contact_container_item_label}>Professional</span>
                <span className={s.contact_container_item_value}>{team?.full_name}</span>
              </div>
              <div className={s.contact_item}>
                <span className={s.contact_container_item_label}>Phone number</span>
                <span className={s.contact_container_item_value}>
                {team?.phone_number?.includes('+') ? '' : '+'}
                  {team?.phone_number}
              </span>
              </div>
              <div className={s.contact_item}>
                <span className={s.contact_container_item_label}>Email</span>
                <span className={s.contact_container_item_value}>{team?.email}</span>
              </div>
              <div className={s.contact_item}>
                <span className={s.contact_container_item_label}>Location</span>
                <span className={s.contact_container_item_value}>
                {data?.services?.salons_profile?.full_address}
              </span>
              </div>
            </div>
            <div className={s.actions}>
              {calendarEvent && (
                  <Button
                      darkGreen
                      wide
                      onClick={() => setShowCalendarModal(true)}
                  >
                    Add to Calendar
                  </Button>
              )}
              <Button
                  wide
                  ligtGrey
                  onClick={() => navigate('/shop')}
              >
                Shop now
              </Button>
            </div>
          </div>
          {showCancel && (
              <CancelAppointment
                  setShowCancel={setShowCancel}
                  handleCancelBooking={handleCancelBooking}
                  canceledOption={canceledOption}
                  cancelNote={cancelNote}
                  setCanceledOption={setCanceledOption}
                  setCancelNote={setCancelNote}
              />
          )}
          {showCalendarModal && calendarEvent && (
              <CalendarModal
                  event={calendarEvent}
                  onClose={() => setShowCalendarModal(false)}
              />
          )}
        </Page>
      </div>
  );
};

export default BookingSuccess;